// src/app/modules/Reports/pages/ReportView.jsx
import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import * as actions from "../../Dashbord/_redux/dashboard/Actions";
import { ArrowLeft, Users, AlertTriangle, Shield, Key, TrendingUp, Calendar } from 'lucide-react';
import Moment from 'moment';
import './report-view.scss';

export function ReportView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { report, loading } = useSelector(
    (state) => ({
      report: state.dashboard.reportDetails,
      loading: state.dashboard.listLoading,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (id) {
      dispatch(actions.fetchAllData('report_details', id));
    }
  }, [id]);

  if (loading && !report) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary"></div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center py-10">
        <h4>Report not found</h4>
        <button className="btn btn-primary mt-3" onClick={() => navigate('/reports')}>
          Back to Reports
        </button>
      </div>
    );
  }

  const visitorAnalytics = report.visitor_analytics || {};
  const incidentAnalytics = report.incident_analytics || {};
  const threatIntel = report.threat_intelligence || {};
  const accessControl = report.access_control || {};
  const securityScore = report.security_score || {};

  return (
    <div className="report-view-page">
      <div className="mb-4">
        <button className="btn btn-light" onClick={() => navigate('/reports')}>
          <ArrowLeft size={16} className="mr-2" />
          Back to Reports
        </button>
      </div>

      {/* Header */}
      <div className="card card-custom mb-5">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h2 className="mb-2">Security Analytics Report</h2>
              <p className="text-muted mb-3">
                <Calendar size={14} className="mr-2" />
                {Moment(report.period.start_date).format('MMM DD, YYYY')} - {Moment(report.period.end_date).format('MMM DD, YYYY')}
              </p>
              <span className="sety-pill sety-pill-purple">{report.report_type}</span>
            </div>
            <div className="text-right">
              <div className="security-score-badge">
                <div className="score-value">{securityScore.overall_score || 0}</div>
                <div className="score-label">Security Score</div>
                <div className={`score-trend ${securityScore.trend}`}>
                  <TrendingUp size={14} className="mr-1" />
                  {securityScore.trend || 'stable'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Visitor Analytics */}
        <div className="col-lg-6 mb-5">
          <div className="card card-custom h-100">
            <div className="card-header">
              <h5 className="mb-0">
                <Users size={18} className="mr-2" />
                Visitor Analytics
              </h5>
            </div>
            <div className="card-body">
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-value">{visitorAnalytics.total_visitors || 0}</div>
                  <div className="stat-label">Total Visitors</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{visitorAnalytics.unique_visitors || 0}</div>
                  <div className="stat-label">Unique Visitors</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{visitorAnalytics.repeat_visitors || 0}</div>
                  <div className="stat-label">Repeat Visitors</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{visitorAnalytics.new_visitors || 0}</div>
                  <div className="stat-label">New Visitors</div>
                </div>
              </div>

              {visitorAnalytics.top_purposes && visitorAnalytics.top_purposes.length > 0 && (
                <div className="mt-4">
                  <h6 className="mb-3">Top Visit Purposes</h6>
                  {visitorAnalytics.top_purposes.map((purpose, idx) => (
                    <div key={idx} className="purpose-item">
                      <span className="purpose-name">{purpose.purpose}</span>
                      <span className="purpose-count">{purpose.count} visits</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4">
                <h6 className="mb-3">Average Visit Duration</h6>
                <div className="duration-display">
                  {visitorAnalytics.average_visit_duration || 0} minutes
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Incident Analytics */}
        <div className="col-lg-6 mb-5">
          <div className="card card-custom h-100">
            <div className="card-header">
              <h5 className="mb-0">
                <AlertTriangle size={18} className="mr-2" />
                Incident Analytics
              </h5>
            </div>
            <div className="card-body">
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-value">{incidentAnalytics.total_incidents || 0}</div>
                  <div className="stat-label">Total Incidents</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{incidentAnalytics.active_incidents || 0}</div>
                  <div className="stat-label">Active Incidents</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{incidentAnalytics.resolution_rate || 0}%</div>
                  <div className="stat-label">Resolution Rate</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{incidentAnalytics.average_response_time || 0}m</div>
                  <div className="stat-label">Avg Response Time</div>
                </div>
              </div>

              {incidentAnalytics.incidents_by_severity && incidentAnalytics.incidents_by_severity.length > 0 ? (
                <div className="mt-4">
                  <h6 className="mb-3">By Severity</h6>
                  {incidentAnalytics.incidents_by_severity.map((item, idx) => (
                    <div key={idx} className="severity-item">
                      <span className={`sety-pill ${
                        item._id === 'critical' ? 'sety-pill-red' :
                        item._id === 'high' ? 'sety-pill-amber' :
                        item._id === 'medium' ? 'sety-pill-purple' : 'sety-pill-green'
                      }`} style={{ marginRight: 8 }}>
                        {item._id}
                      </span>
                      <span>{item.count} incidents</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 text-center text-muted">
                  <AlertTriangle size={32} className="mb-2 opacity-50" />
                  <p>No incidents reported</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Threat Intelligence */}
        <div className="col-lg-6 mb-5">
          <div className="card card-custom h-100">
            <div className="card-header">
              <h5 className="mb-0">
                <Shield size={18} className="mr-2" />
                Threat Intelligence
              </h5>
            </div>
            <div className="card-body">
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-value">{threatIntel.high_risk_visitors_blocked || 0}</div>
                  <div className="stat-label">High Risk Blocked</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{threatIntel.blacklisted_visitors_attempted || 0}</div>
                  <div className="stat-label">Blacklist Attempts</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{threatIntel.suspicious_patterns_detected || 0}</div>
                  <div className="stat-label">Suspicious Patterns</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{threatIntel.network_threats_received || 0}</div>
                  <div className="stat-label">Network Threats</div>
                </div>
              </div>

              {threatIntel.threats_prevented && threatIntel.threats_prevented.length > 0 ? (
                <div className="mt-4">
                  <h6 className="mb-3">Threats Prevented</h6>
                  {threatIntel.threats_prevented.map((threat, idx) => (
                    <div key={idx} className="threat-item">
                      <div className="threat-type">{threat.type}</div>
                      <div className="threat-count">{threat.count}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 text-center text-success">
                  <Shield size={32} className="mb-2" />
                  <p className="mb-0">All systems secure</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Access Control */}
        <div className="col-lg-6 mb-5">
          <div className="card card-custom h-100">
            <div className="card-header">
              <h5 className="mb-0">
                <Key size={18} className="mr-2" />
                Access Control
              </h5>
            </div>
            <div className="card-body">
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-value">{accessControl.total_codes_generated || 0}</div>
                  <div className="stat-label">Codes Generated</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{accessControl.codes_used || 0}</div>
                  <div className="stat-label">Codes Used</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{accessControl.codes_expired || 0}</div>
                  <div className="stat-label">Codes Expired</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{accessControl.codes_revoked || 0}</div>
                  <div className="stat-label">Codes Revoked</div>
                </div>
              </div>

              <div className="mt-4">
                <h6 className="mb-3">Usage Rate</h6>
                <div className="progress" style={{ height: '30px' }}>
                  <div 
                    className="progress-bar bg-primary" 
                    role="progressbar" 
                    style={{ width: `${accessControl.usage_rate || 0}%` }}
                  >
                    {accessControl.usage_rate || 0}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Security Score Breakdown */}
        <div className="col-lg-12 mb-5">
          <div className="card card-custom">
            <div className="card-header">
              <h5 className="mb-0">Security Score Breakdown</h5>
            </div>
            <div className="card-body">
              <div className="row">
                {securityScore.breakdown && Object.entries(securityScore.breakdown).map(([key, value]) => (
                  <div key={key} className="col-md-3 mb-4">
                    <div className="score-breakdown-item">
                      <div className="score-breakdown-label">{key.replace(/_/g, ' ')}</div>
                      <div className="score-breakdown-value">{value}/100</div>
                      <div className="progress mt-2" style={{ height: '8px' }}>
                        <div 
                          className={`progress-bar ${
                            value >= 80 ? 'bg-success' :
                            value >= 60 ? 'bg-info' :
                            value >= 40 ? 'bg-warning' : 'bg-danger'
                          }`}
                          style={{ width: `${value}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {securityScore.comparison_to_network !== undefined && (
                <div className="mt-4 p-4 bg-light rounded">
                  <p className="mb-0 text-center">
                    <strong>Network Comparison:</strong> Your security score is{' '}
                    <span className={securityScore.comparison_to_network >= 0 ? 'text-success' : 'text-danger'}>
                      {Math.abs(securityScore.comparison_to_network)}% {securityScore.comparison_to_network >= 0 ? 'above' : 'below'}
                    </span>{' '}
                    network average
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}